import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Stars from "../components/common/Stars";
import ProductCard from "../components/common/ProductCard";
import { getToken } from "../utils/auth";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../context/CurrencyContext";
import CONFIG from "../config";
import BRANDING from "../branding";
import { getColorHex } from "../utils/colors";
import SEO from "../components/common/SEO";
import { useBranding } from "../context/BrandingContext";
import { useNavigate } from "react-router-dom";

const Products = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const { currency } = useCurrency();
  const [searchParams] = useSearchParams();

  // --- DATA ---
  const [products, setProducts] = useState([]);
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [dimensions, setDimensions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stories, setStories] = useState([]);
  const navigate = useNavigate();


  const { branding } = useBranding();


  // --- FILTER STATES ---
  const [search, setSearch] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");
  const [selectedDimension, setSelectedDimension] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(searchParams.get("category") || "");
  const [priceRange, setPriceRange] = useState([0, 5000]);
  const [maxPriceLimit, setMaxPriceLimit] = useState(5000);
  const [sliderValue, setSliderValue] = useState(5000);

  const [hasVideo, setHasVideo] = useState(false);
  const [hasThumbs, setHasThumbs] = useState(false);
  const [minLikes, setMinLikes] = useState(0);
  const [minRating, setMinRating] = useState(0);
  const [sort, setSort] = useState("");

  // --- PAGINATION ---
  const [page, setPage] = useState(1);
  const limit = 12;
  const [total, setTotal] = useState(0);
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // --- LIKES ---

  // --- AUTO-FETCH UNIQUE FILTER DATA ---
  const loadFilters = useCallback(async () => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/products/filters`, { params: { currency } });
      setColors(res.data?.color || []);
      setMaterials(res.data?.material || []);
      setDimensions(res.data?.dimensions || []);
      setCategories(res.data?.categories || []);
      if (res.data?.priceRange) {
        const mx = Math.ceil(Number(res.data.priceRange.max)) || 5000;
        setPriceRange([0, mx]);
        setMaxPriceLimit(mx);
        setSliderValue(mx);
      }
    } catch (err) {
      console.error("Failed loading filters", err);
    }
  }, [currency]);

  // --- LOAD PRODUCTS ---
  const loadProducts = useCallback(async () => {
    try {
      const res = await axios.get(`${CONFIG.API_URL}/api/products`, {
        params: {
          search,
          color: selectedColor,
          material: selectedMaterial,
          dimensions: selectedDimension,
          category: selectedCategory,
          minPrice: priceRange[0],
          maxPrice: priceRange[1],
          hasVideo,
          hasThumbnails: hasThumbs,
          minLikes,
          minRating,
          sort,
          currency,
          page,
          per: limit,
          language: i18n.language
        },
        headers: getToken() ? { Authorization: `Bearer ${getToken()}` } : {}
      });
      setProducts(res.data.data || []);
      setTotal(res.data.meta?.total || res.data.total || 0);
    } catch (err) {
      console.error("Failed to load products", err);
    }
  }, [search, selectedColor, selectedMaterial, selectedDimension, selectedCategory, priceRange, hasVideo, hasThumbs, minLikes, minRating, sort, currency, page, i18n.language]);

  useEffect(() => {
    loadFilters();
  }, [loadFilters]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Sync category filter with URL params
  useEffect(() => {
    const categoryFromUrl = searchParams.get("category") || "";
    if (categoryFromUrl !== selectedCategory) {
      setSelectedCategory(categoryFromUrl);
    }
  }, [searchParams, selectedCategory]);

  useEffect(() => {
    setPage(1);
  }, [search, selectedColor, selectedMaterial, selectedDimension, selectedCategory, priceRange, hasVideo, hasThumbs, minLikes, minRating, sort, currency]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPriceRange([0, sliderValue]);
    }, 400);
    return () => clearTimeout(timer);
  }, [sliderValue]);

  // ✅ Fetch stories on load (public - no auth required)
  useEffect(() => {
    const fetchStories = async () => {
      try {
        const token = getToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        const res = await axios.get(`${CONFIG.API_URL}/api/stories`, { headers });
        setStories(res.data);
      } catch (err) {
        console.log("Failed to fetch stories.");
      }
    };
    fetchStories();
  }, []);

  // --- MOBILE FILTER STATE ---
  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <>

      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
        <title>
          {isArabic
            ? `تسوق الأثاث الراقي | ${BRANDING.appName.ar}`
            : `Shop Luxury Furniture | ${BRANDING.appName.en}`}
        </title>
        <meta name="description" content={isArabic ? BRANDING.seo.defaultDescription.ar : BRANDING.seo.defaultDescription.en} />
      </Helmet>
      <SEO />

      {/* Mobile Filter Toggle */}
      <div className={`lg:hidden fixed bottom-10 ${isArabic ? "left-6" : "right-6"} z-40`}>
        <button
          onClick={() => setIsFilterOpen(true)}
          className="bg-brand-primary text-white p-4 rounded-full shadow-2xl flex items-center gap-2 hover:scale-110 active:scale-95 transition-all"
        >
          <span className="text-xl"><i className="fas fa-sliders-h"></i></span>
          <span className="font-bold">{t('shop.filters.title', 'Filters')}</span>
        </button>
      </div>

      <div className="flex flex-col lg:flex-row max-w-7xl mx-auto mt-6 gap-8 px-4 lg:px-8 relative pb-20">

        {/* Mobile Overlay */}
        {isFilterOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-[60] lg:hidden backdrop-blur-sm transition-opacity"
            onClick={() => setIsFilterOpen(false)}
          />
        )}

        {/* Sidebar / Mobile Drawer */}
        <aside className={`
          fixed lg:sticky top-0 lg:top-24
          ${isArabic ? "right-0 lg:right-auto" : "left-0 lg:left-auto"}
          h-full lg:h-fit w-full sm:w-80 lg:w-72
          bg-white shadow-2xl lg:shadow-none p-6 lg:p-0 
          z-[70] lg:z-10
          transition-transform duration-500 ease-in-out
          overflow-y-auto lg:overflow-visible
          ${isFilterOpen ? "translate-x-0" : (isArabic ? "translate-x-full lg:translate-x-0" : "-translate-x-full lg:translate-x-0")}
        `}>

          <div className="flex justify-between items-center mb-8 lg:hidden">
            <h3 className="font-black text-2xl tracking-tight">{t('shop.filters.title', 'Filters')}</h3>
            <button onClick={() => setIsFilterOpen(false)} className="text-3xl text-gray-400 hover:text-red-500 transition-colors">&times;</button>
          </div>

          <h3 className="hidden lg:block font-black text-xs uppercase tracking-widest text-brand-primary mb-6 border-b pb-4">{t('shop.filters.title', 'Filters')}</h3>

          {/* Search */}
          <div className="mb-8">
            <label className="block font-bold mb-3 text-[10px] text-gray-400 uppercase tracking-widest">{t('shop.filters.search', 'Search')}</label>
            <div className="relative group">
              <input
                className="bg-gray-50 border-none p-4 pl-12 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-medium placeholder:text-gray-300"
                placeholder={t('shop.filters.search_placeholder', 'Search products...')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300 group-focus-within:text-brand-primary transition-colors">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </div>

          {/* Color Filter */}
          <div className="mb-8">
            <label className="block font-bold mb-4 text-[10px] text-gray-400 uppercase tracking-widest">{t('shop.filters.color', 'Color')}</label>
            <div className="flex flex-wrap gap-2.5">
              {colors?.map((c) => (
                <button
                  key={c.value}
                  className={`w-9 h-9 rounded-full border-2 transition-all hover:scale-110 relative p-0.5 ${selectedColor === c.value ? "border-brand-primary shadow-lg ring-2 ring-brand-primary/10" : "border-gray-100 hover:border-brand-primary/30"}`}
                  style={{ backgroundColor: getColorHex(c.value) }}
                  onClick={() => setSelectedColor(selectedColor === c.value ? "" : c.value)}
                  title={isArabic && c.name_ar ? c.name_ar : c.name}
                >
                  {selectedColor === c.value && (
                    <span className={`absolute inset-0 flex items-center justify-center text-[10px] ${c.value === '#FFFFFF' ? 'text-black' : 'text-white'}`}>
                      <i className="fas fa-check"></i>
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Select Filters */}
          <div className="space-y-8">
            {[
              { label: branding?.filterPlaceholders?.category?.label?.[isArabic ? 'ar' : 'en'] || t('shop.filters.category'), value: selectedCategory, setter: setSelectedCategory, options: categories, placeholder: branding?.filterPlaceholders?.category?.selectTypeAndOption?.[isArabic ? 'ar' : 'en'] || t('shop.filters.all_categories', 'All Categories'), isCat: true, icon: branding?.filterPlaceholders?.category?.icon || 'fa-layer-group' },
              { label: branding?.filterPlaceholders?.material?.label?.[isArabic ? 'ar' : 'en'] || t('shop.filters.material'), value: selectedMaterial, setter: setSelectedMaterial, options: materials, placeholder: branding?.filterPlaceholders?.material?.selectTypeAndOption?.[isArabic ? 'ar' : 'en'] || t('shop.filters.all_materials', 'All Materials'), icon: branding?.filterPlaceholders?.material?.icon || 'fa-couch' },
              { label: branding?.filterPlaceholders?.dimensions?.label?.[isArabic ? 'ar' : 'en'] || t('shop.filters.dimensions'), value: selectedDimension, setter: setSelectedDimension, options: dimensions, placeholder: branding?.filterPlaceholders?.dimensions?.selectTypeAndOption?.[isArabic ? 'ar' : 'en'] || t('shop.filters.all_dimensions', 'All Dimensions'), icon: branding?.filterPlaceholders?.dimensions?.icon || 'fa-ruler-horizontal' },
            ].map((f) => (
              <div key={f.label}>
                <label className="block font-bold mb-3 text-[10px] text-gray-400 uppercase tracking-widest flex items-center gap-2">
                  <i className={`fas ${f.icon} text-brand-primary/40`}></i> {f.label}
                </label>
                <div className="relative">
                  <select
                    className="appearance-none bg-gray-50 border-none p-4 w-full rounded-2xl focus:bg-white focus:ring-2 focus:ring-brand-primary/20 transition-all font-bold text-sm cursor-pointer pr-10"
                    value={f.value}
                    onChange={(e) => f.setter(e.target.value)}
                  >
                    <option value="">{f.placeholder}</option>
                    {(f.options || []).map((o, idx) => (
                      <option key={f.isCat ? o.id : `${o.value}-${idx}`} value={f.isCat ? o.id : o.value}>
                        {isArabic && o.name_ar ? o.name_ar : o.name}
                      </option>
                    ))}
                  </select>
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none text-xs"><i className="fas fa-chevron-down"></i></span>
                </div>
              </div>
            ))}

            {/* Rating Filter (Stylized) */}
            <div>
              <label className="block font-bold mb-3 text-[10px] text-gray-400 uppercase tracking-widest flex items-center gap-2">
                <i className="fas fa-star text-brand-primary/40"></i> {t('shop.filters.min_rating', 'Minimum Rating')}
              </label>
              <div className="flex flex-wrap gap-2">
                {[5, 4, 3, 2, 0].map((r) => (
                  <button
                    key={r}
                    onClick={() => setMinRating(r)}
                    className={`
                      px-3 py-2 rounded-xl border text-[10px] font-black transition-all flex items-center gap-1.5
                      ${minRating === r ? "bg-brand-primary text-white border-brand-primary shadow-lg shadow-brand-primary/20 scale-105" : "bg-gray-50 text-gray-400 border-gray-100 hover:border-brand-primary/30"}
                    `}
                  >
                    {r === 0 ? (
                      t('shop.sort_options.default', 'All')
                    ) : (
                      <>
                        {r}+ <Stars value={r} />
                      </>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Price Range */}
          <div className="mt-10 mb-10">
            <label className="block font-bold mb-6 text-[10px] text-gray-400 uppercase tracking-widest">{t('shop.price_range')}</label>
            <input
              type="range"
              min="0"
              max={maxPriceLimit}
              step="1"
              value={sliderValue}
              onChange={(e) => setSliderValue(Number(e.target.value))}
              className="w-full accent-brand-primary h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer"
            />
            <div className="flex justify-between items-center mt-6">
              <span className="bg-gray-50 text-gray-400 px-4 py-2 rounded-2xl text-[10px] font-black tracking-tighter">0</span>
              <div className="flex flex-col items-end">
                <span className="text-brand-primary text-sm font-black tracking-tight">{new Intl.NumberFormat(i18n.language, { style: 'currency', currency: currency || 'USD', currencyDisplay: 'code', minimumFractionDigits: 0 }).format(sliderValue)}</span>
                <span className="text-[8px] text-gray-300 uppercase font-black tracking-widest">{t('shop.filters.max_limit', 'Max Limit')}</span>
              </div>
            </div>
          </div>

          {/* Toggle Filters */}
          <div className="space-y-3 pt-8 border-t border-gray-100">
            {[
              { checked: hasVideo, setter: () => setHasVideo(!hasVideo), label: t('shop.filters.has_video'), icon: 'fa-play-circle' },
              { checked: hasThumbs, setter: () => setHasThumbs(!hasThumbs), label: t('shop.filters.has_thumbnails'), icon: 'fa-images' }
            ].map((item, i) => (
              <label key={i} className="flex items-center justify-between p-3 rounded-2xl hover:bg-gray-50 transition-colors cursor-pointer group">
                <div className="flex items-center gap-3">
                  <i className={`fas ${item.icon} text-gray-200 group-hover:text-brand-primary/40 transition-colors`}></i>
                  <span className="text-xs font-bold text-gray-500">{item.label}</span>
                </div>
                <div className={`w-10 h-6 rounded-full transition-all relative ${item.checked ? 'bg-brand-primary' : 'bg-gray-200'}`}>
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${item.checked ? (isArabic ? 'left-1' : 'right-1') : (isArabic ? 'right-1' : 'left-1')}`} />
                </div>
                <input type="checkbox" className="hidden" checked={item.checked} onChange={item.setter} />
              </label>
            ))}
          </div>

          {/* Reset Buttons */}
          <div className="mt-10 space-y-3">
            <button
              className="text-gray-400 w-full py-4 rounded-2xl hover:bg-red-50 hover:text-red-500 transition-all text-[10px] font-black uppercase tracking-widest border border-dashed border-gray-200 hover:border-red-200"
              onClick={() => {
                setSelectedColor(""); setSelectedMaterial(""); setSelectedDimension(""); setSelectedCategory(""); setSearch(""); setPriceRange([0, maxPriceLimit]); setSliderValue(maxPriceLimit); setHasVideo(false); setHasThumbs(false); setMinLikes(0); setMinRating(0); setSort("");
                if (window.innerWidth < 1024) setIsFilterOpen(false);
              }}
            >
              {t('shop.reset_filters')}
            </button>
            <button
              className="bg-brand-primary text-white w-full py-5 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-brand-primary/20 hover:scale-[1.02] active:scale-95 transition-all lg:hidden"
              onClick={() => setIsFilterOpen(false)}
            >
              {t('shop.apply_filters', 'Apply Filters')}
            </button>
          </div>
        </aside>

        {/* Main Content */}
        <div className="flex-1 min-w-0">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-10 gap-6">
            <div>
              <h2 className="text-3xl font-black text-gray-900 tracking-tight mb-2">{t('shop.products')}</h2>
              <p className="text-gray-400 text-xs font-bold uppercase tracking-widest">
                {t('shop.results_count', { count: products.length, total: total })}
              </p>
            </div>

            <div className="relative min-w-[200px]">
              <select
                className="appearance-none bg-white border border-gray-100 p-4 w-full rounded-2xl font-bold text-sm cursor-pointer pr-10 shadow-sm focus:ring-2 focus:ring-brand-primary/10 transition-all"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
              >
                <option value="">{t('shop.sort_options.default')}</option>
                <option value="price_asc">{t('shop.sort_options.price_asc')}</option>
                <option value="price_desc">{t('shop.sort_options.price_desc')}</option>
                <option value="newest">{t('shop.sort_options.newest')}</option>
                <option value="popular">{t('shop.sort_options.popular')}</option>
              </select>
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-300 pointer-events-none"><i className="fas fa-sort-amount-down"></i></span>
            </div>
          </div>

          {/* Grid */}
          {products.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {products.map((p) => (
                <ProductCard
                  key={p.id}
                  product={p}
                  isAr={isArabic}
                />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center bg-gray-50 rounded-3xl border border-dashed border-gray-200">
              <div className="text-6xl text-gray-200 mb-6"><i className="fas fa-couch"></i></div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">{t('shop.no_items', 'No items found')}</h3>
              <p className="text-gray-500 text-sm max-w-xs mx-auto">{t('shop.no_items_desc', 'Try adjusting your filters or search keywords to find what you\'re looking for.')}</p>
            </div>
          )}

          {/* ✨ Stories Carousel - shown above product grid */}
          {stories.length > 0 && (
            <div style={{ marginBottom: '32px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <span style={{ fontSize: '22px' }}>🎬</span>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#1a1a2e' }}>AI Product Stories</h3>
                </div>
                <a href="/stories" style={{
                  fontSize: '13px', fontWeight: 700, color: 'var(--color-brand-primary, #7c3aed)',
                  textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px'
                }}>View all <i className="fas fa-arrow-right" style={{ fontSize: '11px' }}></i></a>
              </div>
              <div style={{
                display: 'flex', gap: '16px', overflowX: 'auto',
                paddingBottom: '12px', scrollbarWidth: 'thin',
                scrollSnapType: 'x mandatory'
              }}>
                {stories.map((story, i) => (
                  <div key={story.id} style={{
                    minWidth: '220px', maxWidth: '220px', borderRadius: '16px',
                    overflow: 'hidden', scrollSnapAlign: 'start',
                    background: `linear-gradient(135deg, hsl(${(i * 67) % 360}, 65%, 15%), hsl(${(i * 67 + 90) % 360}, 70%, 10%))`,
                    border: '1px solid rgba(255,255,255,0.08)',
                    flexShrink: 0, cursor: 'pointer',
                    transition: 'transform 0.25s ease',
                  }}
                    onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                    onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
                    onClick={() => navigate('/stories')}
                  >
                    {story.videoUrl ? (
                      <video
                        style={{ width: '100%', height: '140px', objectFit: 'cover', display: 'block' }}
                        muted autoPlay loop playsInline preload="metadata"
                      >
                        <source src={story.videoUrl?.startsWith('http') ? story.videoUrl : `${CONFIG.API_URL}${story.videoUrl}`} type="video/mp4" />
                      </video>
                    ) : (
                      <div style={{
                        width: '100%', height: '140px', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '36px', flexDirection: 'column', gap: '8px'
                      }}>
                        <span>🎬</span>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>Processing...</span>
                      </div>
                    )}
                    <div style={{ padding: '12px' }}>
                      <p style={{
                        color: '#fff', fontWeight: 700, fontSize: '13px',
                        margin: 0, whiteSpace: 'nowrap', overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>{story.title}</p>
                      <p style={{
                        color: 'rgba(255,255,255,0.5)', fontSize: '11px',
                        margin: '4px 0 0', whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis'
                      }}>{story.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center items-center gap-3 mt-20">
              <button
                onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={page === 1}
                className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-brand-primary hover:border-brand-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className={`fas fa-chevron-${isArabic ? "right" : "left"}`}></i>
              </button>

              <div className="flex gap-2.5">
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => { setPage(i + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                    className={`w-12 h-12 flex items-center justify-center rounded-2xl font-black text-sm transition-all ${page === i + 1 ? "bg-brand-primary text-white shadow-xl shadow-brand-primary/30 scale-110" : "bg-white border border-gray-100 text-gray-400 hover:bg-gray-50 hover:text-gray-600"}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={page === totalPages}
                className="w-12 h-12 flex items-center justify-center rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-brand-primary hover:border-brand-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
              >
                <i className={`fas fa-chevron-${isArabic ? "left" : "right"}`}></i>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default React.memo(Products);
